import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { ExaminerService } from '@tsin-core/services/http/examiner.service';
import {
  ClearExaminers,
  CreateExaminer,
  CreateExamPool,
  DeleteExaminer,
  EmptyAssignedExaminers,
  ExaminerSignInOut,
  GetAssignedExaminers,
  GetExaminers,
  GetExaminersAvaliability,
  GetExaminerByUserId,
  SendAvailabilityEmail,
  UpdateAvailability,
  UpdateExaminer,
  UpdateExaminerStatus,
  GetExaminerSignInAction,
} from './examiner.action';
import { catchError, tap, throwError } from 'rxjs';
import { LoadingState } from '@tsin-core/entities';
import { IExaminer } from '@tsin-core/models/examiner.model';

export interface ExaminerStateModel {
  examiner: IExaminer[];
  assignedExaminers: IExaminer[];
  singleExaminer: IExaminer[];
  signInHistory: any[];
  loading: LoadingState;
  loadingExaminers: boolean;
}

@State<ExaminerStateModel>({
  name: 'examinerState',
  defaults: {
    examiner: [],
    assignedExaminers: [],
    singleExaminer: [],
    signInHistory: [],
    loadingExaminers: false,
    loading: LoadingState.notLoading,
  },
})

@Injectable()
export class ExaminerState {
  examinerService: ExaminerService = inject(ExaminerService);

  @Selector()
  static listExaminers(state: ExaminerStateModel): IExaminer[] {
    return state?.examiner ?? [];
  }

  @Selector()
  static listAssignedExaminers(state: ExaminerStateModel): IExaminer[] {
    return state?.assignedExaminers ?? [];
  }

  @Selector()
  static getloadingExaminers(state: ExaminerStateModel) {
    return state.loadingExaminers;
  }

  @Selector()
  static getSingleExaminer(state: ExaminerStateModel) {
    return state.singleExaminer;
  }

  @Selector()
  static getSignInHistory(state: ExaminerStateModel) {
    return state.signInHistory;
  }

  @Selector()
  static getLoading(state: ExaminerStateModel): LoadingState {
    return state.loading;
  }

  @Action(CreateExaminer)
  createExaminer(
    ctx: StateContext<ExaminerStateModel>,
    action: CreateExaminer
  ) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.examinerService.createExaminer(action.payload).pipe(
      tap((newExaminer: any) => {
        const state = ctx.getState();
        ctx.patchState({
          examiner: [...state.examiner, newExaminer],
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UpdateExaminer)
  updateExaminer(
    ctx: StateContext<ExaminerStateModel>,
    action: UpdateExaminer
  ) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });

    return this.examinerService.updateExaminer(action.updatePayload).pipe(
      tap((updatedExaminer: IExaminer) => {
        const state = ctx.getState();

        const updatedExaminers = state.examiner.map((examiner) =>
          examiner.id === updatedExaminer.id ? updatedExaminer : examiner
        );

        ctx.patchState({
          examiner: updatedExaminers,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        return throwError(() => error);
      })
    );
  }

  @Action(GetExaminers)
  getExaminers(ctx: StateContext<ExaminerStateModel>, action: GetExaminers) {
    ctx.patchState({ loading: LoadingState.loadingList });
    ctx.patchState({ loadingExaminers: true });
    return this.examinerService.getExaminers(action.payload).pipe(
      tap((examiner: any[]) => {
        ctx.patchState({
          examiner,
          loading: LoadingState.notLoading,
          loadingExaminers: false,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingState.notLoading,
          loadingExaminers: false,
        });
        return throwError(() => error);
      })
    );
  }


  @Action(ExaminerSignInOut)
  examinerSignInOut(ctx: StateContext<ExaminerStateModel>, action: ExaminerSignInOut) {
    ctx.patchState({ loading: LoadingState.notLoading });
    return this.examinerService.sigInOutExaminer(action.payload).pipe(
      tap((newSignIn: any) => {
        const state = ctx.getState();

        // console.log('Sign IN Returned', newExaminer);
        ctx.patchState({
          // examiner: [...state.examiner, newExaminer],
          signInHistory: [...state.signInHistory, newSignIn],
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(GetExaminerSignInAction)
  getExaminerSignInOut(ctx: StateContext<ExaminerStateModel>, action: GetExaminerSignInAction) {
    ctx.patchState({ loading: LoadingState.notLoading });
    return this.examinerService.sigInOutExaminerHistory(action.payload).pipe(
      tap((examinerHistory: any) => {
        const state = ctx.getState();

        // console.log('Sign IN History', examinerHistory);
        ctx.patchState({
          // examiner: [...state.examiner, newExaminer],
          signInHistory: examinerHistory,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }


  @Action(GetExaminerByUserId)
  getExaminerByUserId(ctx: StateContext<ExaminerStateModel>, action: GetExaminerByUserId) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.examinerService.getExaminerByUserId(action.payload).pipe(
      tap((singleExaminer: IExaminer[]) => {
        const state = ctx.getState();

        // console.log('Sign IN Returned', singleExaminer);
        ctx.patchState({
          singleExaminer: singleExaminer,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );

  }


  @Action(ClearExaminers)
  clearExaminersList(ctx: StateContext<ExaminerStateModel>, action: ClearExaminers) {
    ctx.patchState({ loading: LoadingState.notLoading, examiner: undefined });
  }


  @Action(GetExaminersAvaliability)
  getExaminersAvaliability(ctx: StateContext<ExaminerStateModel>, action: GetExaminersAvaliability) {
    ctx.patchState({ loading: LoadingState.loadingList, loadingExaminers: true });
    return this.examinerService.getExaminersAvailability(action.payload).pipe(
      tap((examiner: any[]) => {
        console.log('Examiner Availability', examiner)
        ctx.patchState({
          examiner,
          loading: LoadingState.notLoading,
          loadingExaminers: false,
        });
      }),
      catchError((error) => {
        ctx.patchState({
          loading: LoadingState.notLoading,
          loadingExaminers: false,
        });
        return throwError(() => error);
      })
    );
  }

  @Action(GetAssignedExaminers)
  getAssignedExaminers(
    ctx: StateContext<ExaminerStateModel>,
    action: GetAssignedExaminers
  ) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.examinerService.getAssignedExaminers(action.examId).pipe(
      tap((assignedExaminers: IExaminer[]) => {
        ctx.patchState({
          assignedExaminers: assignedExaminers.filter((examiner) => examiner.type == action.examType),
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        return throwError(() => error);
      })
    );
  }

  @Action(DeleteExaminer)
  deleteExaminer(
    ctx: StateContext<ExaminerStateModel>,
    action: DeleteExaminer
  ) {
    ctx.patchState({ loading: LoadingState.loadingDelete });

    return this.examinerService.deleteExaminer(action.id).pipe(
      tap(() => {
        const state = ctx.getState();
        const updatedExaminers = state.examiner.filter(
          (examiner) => examiner.id !== action.id
        );

        ctx.patchState({
          examiner: updatedExaminers,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        return throwError(() => error);
      })
    );
  }

  @Action(SendAvailabilityEmail)
  sendEmail(
    ctx: StateContext<ExaminerStateModel>,
    action: SendAvailabilityEmail
  ) {
    const state = ctx.getState();

    return this.examinerService.sendAvailEmail(action.input).pipe(
      tap(() => {
        ctx.patchState({});
      }),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  @Action(CreateExamPool)
  createExamPool(ctx: StateContext<ExaminerStateModel>, action: CreateExamPool) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    console.log('Creating Exam Pool', action.payload);
    return this.examinerService.createExamPool(action.payload).pipe(
      tap((newExamPool: any) => {

        console.log('New Exam Pool', newExamPool)
        const state = ctx.getState();
        ctx.patchState({
          // exams: [...state.exams, newExamPool],
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(EmptyAssignedExaminers)
  emptyExaminerList(ctx: StateContext<ExaminerStateModel>, action: EmptyAssignedExaminers) {
    ctx.patchState({ assignedExaminers: [] });
  }

  @Action(UpdateAvailability)
  updateAvail(
    ctx: StateContext<ExaminerStateModel>,
    action: UpdateAvailability
  ) {
    const state = ctx.getState();

    return this.examinerService.updateAvail(action.input).pipe(
      tap((response: any) => {
        console.log('response', response);
        console.log('all examiners', state.assignedExaminers)
        if (state.singleExaminer.length > 0) {

          ctx.patchState({
            singleExaminer: state.singleExaminer.map((examin) =>
              examin.id === action.input.id ? { ...examin, ...response } : examin
            ),
          });
        }
        console.log('updated examiner', state.assignedExaminers.find((examin) =>
          examin.id === action.input.id))
        ctx.patchState({
          assignedExaminers: state.assignedExaminers.map((examin) =>
            examin.id === action.input.id ? response : examin
          ),
        });

      }),
      catchError((err) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw err;
      })
    );
  }


  @Action(UpdateExaminerStatus)
  updateExaminerStatus(
    ctx: StateContext<ExaminerStateModel>, action: UpdateExaminerStatus) {
    const state = ctx.getState();

    return this.examinerService.updateExaminerStatus(action.payload).pipe(
      tap((response: any) => {
        // console.log('response', response);
        // console.log('all examiners', state.examiner)
        if (state.examiner.length > 0) {
          ctx.patchState({
            examiner: state.examiner.map((examin) =>
              examin.id === action.payload.examinerId ? { ...examin, ...response } : examin
            ),
          });
        }
        console.log('updated examiner', state.examiner.find((examiner) =>
          examiner.id === action.payload.examinerId))
        ctx.patchState({
          assignedExaminers: state.assignedExaminers.map((examin) =>
            examin.id === action.payload.examinerId ? { examin, ...response } : examin
          ),
        });

      }),
      catchError((err) => {
        console.log('error response', err);

        ctx.patchState({ loading: LoadingState.notLoading });
        throw err;
      })
    );
  }


}
